import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Clock, Inbox, Layers } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useSystemHealthStore } from '../store/systemHealthStore';
import { StatKpiCard } from './StatKpiCard';

const OLDEST_WARN_SECONDS = 120;

function formatAge(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/**
 * "Right now" outbox tiles (86d3peur0 Phase C) — subscribes to the `liveStats` slice ONLY (fine-grained
 * selector), so the 5–10s poll re-renders these four cards without touching the trend charts. The oldest-
 * pending age is extrapolated client-side (ticks every 1s from the last poll's anchor) so it moves smoothly
 * between polls, and turns amber past the 2-minute target.
 */
export function OutboxLiveTiles() {
  const liveStats = useSystemHealthStore((s) => s.liveStats);

  // Anchor the oldest-pending age to the moment its poll arrived, then tick locally.
  const anchorRef = useRef<{ base: number; at: number } | null>(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    anchorRef.current =
      liveStats?.oldestPendingAgeSeconds != null
        ? { base: liveStats.oldestPendingAgeSeconds, at: Date.now() }
        : null;
  }, [liveStats]);

  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!liveStats) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-xl border p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      </div>
    );
  }

  const oldestAge = anchorRef.current
    ? anchorRef.current.base + (Date.now() - anchorRef.current.at) / 1000
    : null;
  const oldestOverTarget = oldestAge != null && oldestAge > OLDEST_WARN_SECONDS;
  const hasDeadLetters = liveStats.deadLettered > 0;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatKpiCard
        title="Pending jobs"
        titleTip="Outbox posting jobs waiting to be claimed by the dispatcher. Source: OutboxJobs where Status = Pending."
        value={liveStats.pending}
        subtitle="Awaiting the dispatcher"
        icon={Inbox}
        tone="info"
      />
      <StatKpiCard
        title="Oldest pending"
        titleTip="How long the oldest Pending job has waited. Ticks live; target under 2 minutes. Source: now − min(CreatedAt) of Pending jobs."
        value={oldestAge != null ? formatAge(oldestAge) : '—'}
        subtitle="Target < 2m"
        icon={Clock}
        tone={oldestOverTarget ? 'warning' : 'info'}
        valueClassName={oldestOverTarget ? 'text-warning' : undefined}
      />
      <StatKpiCard
        title="In flight"
        titleTip="Jobs currently claimed by the dispatcher and posting to NetSuite. Source: OutboxJobs where Status = Claimed."
        value={liveStats.inFlight}
        subtitle="Being posted now"
        icon={Layers}
        tone="info"
      />
      <StatKpiCard
        title="Dead letters"
        titleTip="Jobs that exhausted their retries and need an admin Requeue or Cancel. Source: OutboxJobs where Status = Failed."
        value={liveStats.deadLettered}
        subtitle="Retries exhausted"
        icon={AlertTriangle}
        tone={hasDeadLetters ? 'destructive' : 'info'}
        valueClassName={hasDeadLetters ? 'text-destructive' : undefined}
        footer={hasDeadLetters ? <span className="text-destructive">Needs attention</span> : undefined}
      />
    </div>
  );
}
